"use client";

/**
 * Button — shadcn/ui 기반 버튼 프리미티브
 *
 * variant: 시각 스타일 (default / destructive / outline / secondary / ghost / link)
 * size: 크기 (default / sm / lg / icon)
 * asChild: true 이면 <button> 대신 자식 엘리먼트(예: <a>, <Link>)에 스타일을 입힌다.
 * loading: true 이면 스피너를 표시하고 버튼을 자동으로 비활성화한다.
 *
 * @example
 * <Button>저장</Button>
 * <Button variant="outline" size="sm">취소</Button>
 * <Button loading={isSaving}>저장 중</Button>
 * <Button disabled>비활성</Button>
 * <Button asChild><Link href="/new">등록</Link></Button>
 */
import { type ComponentProps } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-ring aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-12 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
    className,
    variant,
    size,
    asChild = false,
    loading = false,
    disabled,
    children,
    ...props
}: ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
        /** true 이면 스피너를 표시하고 버튼을 자동 비활성화 */
        loading?: boolean;
    }) {
    const Comp = asChild ? Slot : "button";
    return (
        <Comp
            data-slot="button"
            disabled={disabled || loading}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        >
            {loading && <Loader2 className="animate-spin" />}
            {children}
        </Comp>
    );
}

export { Button, buttonVariants };
